/**
 * Versão mobile do perfil Manutenção — regras puras (sem React, sem `window`).
 *
 * Decisão do cliente (21/10/2026): o perfil Manutenção tem DOIS layouts.
 * - computador: o de sempre (menu lateral, `AppShell`);
 * - celular e tablet: casca mobile (cabeçalho, barra inferior / abas, menu "Mais").
 *
 * As ROTAS são as mesmas (`/frota/manutencao/...`): só muda a moldura. Assim um
 * link compartilhado abre a mesma tela nos dois aparelhos, e nenhuma tela existe
 * duas vezes.
 */
import type { AuthUser } from "@bi/types";

/** O que o usuário escolheu. `auto` = decide pelo aparelho. */
export type LayoutPreference = "auto" | "mobile" | "desktop";
export type Layout = "mobile" | "desktop";

export const LAYOUT_STORAGE_KEY = "siag:manutencao:layout";

/** Abaixo disto o menu lateral de 256 px não cabe — mobile em qualquer aparelho. */
export const MOBILE_MAX_WIDTH = 768;
/** Tela de toque até este tamanho é celular ou tablet (iPad Pro deitado = 1366). */
export const TOUCH_MAX_WIDTH = 1366;

export interface Device {
  /** largura da janela, em px CSS */
  width: number;
  /** ponteiro principal é o dedo (`(pointer: coarse)`) */
  coarsePointer: boolean;
}

/** Quem tem a versão mobile. Hoje: o perfil Manutenção (pedido do cliente). */
export function hasMobileLayout(user: Pick<AuthUser, "role"> | null | undefined): boolean {
  return user?.role === "manutencao";
}

export function resolveLayout(pref: LayoutPreference, device: Device): Layout {
  if (pref !== "auto") return pref;
  if (device.width < MOBILE_MAX_WIDTH) return "mobile";
  if (device.coarsePointer && device.width <= TOUCH_MAX_WIDTH) return "mobile";
  return "desktop";
}

export function parsePreference(raw: string | null | undefined): LayoutPreference {
  return raw === "mobile" || raw === "desktop" ? raw : "auto";
}

/**
 * Troca manual. Escolher o que o aparelho já escolheria volta para `auto`: assim
 * quem troca e destroca não fica preso numa preferência fixa sem saber.
 */
export function preferenceFor(target: Layout, device: Device): LayoutPreference {
  return resolveLayout("auto", device) === target ? "auto" : target;
}

// ── Navegação ────────────────────────────────────────────────────────────────

const BASE = "/frota/manutencao";

export type MobileNavKey = "central" | "preventiva" | "ordens" | "checklists" | "mais";

export interface MobileNavItem {
  key: MobileNavKey;
  label: string;
  to: string;
}

/** Barra inferior do celular: o que se usa no pátio, mais o "Mais". */
export const MOBILE_BOTTOM_NAV: MobileNavItem[] = [
  { key: "central", label: "Central", to: BASE },
  { key: "preventiva", label: "Preventiva", to: `${BASE}/preventiva` },
  { key: "ordens", label: "O.S.", to: `${BASE}/ordens` },
  { key: "checklists", label: "Checklists", to: `${BASE}/checklists` },
  { key: "mais", label: "Mais", to: "" },
];

export interface MoreItem {
  label: string;
  to: string;
  icon: "shield" | "erp" | "templates" | "groups" | "register" | "costs";
}

/** O resto do módulo — tudo o que está no menu lateral do computador e não cabe na barra. */
export const MOBILE_MORE_ITEMS: MoreItem[] = [
  { label: "Registrar manutenção", to: `${BASE}/registrar`, icon: "register" },
  { label: "Não Conformidades", to: `${BASE}/nao-conformidades`, icon: "shield" },
  { label: "OS do ERP", to: `${BASE}/erp`, icon: "erp" },
  { label: "Modelos de Checklist", to: `${BASE}/modelos`, icon: "templates" },
  { label: "Grupos de Veículos", to: `${BASE}/grupos`, icon: "groups" },
  { label: "Custos de manutenção", to: `${BASE}/custos`, icon: "costs" },
];

/** Abas do tablet: todas as telas do menu lateral. */
export const MOBILE_TABS: Array<{ label: string; to: string }> = [
  { label: "Central", to: BASE },
  { label: "Preventiva", to: `${BASE}/preventiva` },
  { label: "Ordens de Serviço", to: `${BASE}/ordens` },
  { label: "Checklists", to: `${BASE}/checklists` },
  { label: "Não Conformidades", to: `${BASE}/nao-conformidades` },
  { label: "OS do ERP", to: `${BASE}/erp` },
  { label: "Modelos", to: `${BASE}/modelos` },
  { label: "Grupos", to: `${BASE}/grupos` },
];

function clean(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/** Item da barra inferior aceso para um caminho. */
export function activeBottomKey(pathname: string): MobileNavKey {
  const p = clean(pathname);
  if (p === BASE) return "central";
  if (p.startsWith(`${BASE}/preventiva`)) return "preventiva";
  if (p.startsWith(`${BASE}/ordens`) || p.startsWith(`${BASE}/nova`)) return "ordens";
  if (p.startsWith(`${BASE}/checklists`)) return "checklists";
  return "mais";
}

/** Aba acesa no tablet — a mais específica que casar. */
export function activeTab(pathname: string): string | null {
  const p = clean(pathname);
  const casou = MOBILE_TABS.filter((t) => p === t.to || (t.to !== BASE && p.startsWith(`${t.to}/`)));
  return casou.sort((a, b) => b.to.length - a.to.length)[0]?.to ?? null;
}

/** Título do cabeçalho e se a tela é "filha" (mostra voltar em vez do logo). */
export function screenInfo(pathname: string): { title: string; child: boolean } {
  const p = clean(pathname);
  const r = p.startsWith(`${BASE}/`) ? p.slice(BASE.length + 1) : "";
  const [a, b] = r.split("/");
  if (!a) return { title: "Central de Manutenção", child: false };
  const TOPO: Record<string, string> = {
    preventiva: "Manutenção Preventiva",
    ordens: "Ordens de Serviço",
    checklists: "Checklists",
    "nao-conformidades": "Não Conformidades",
    erp: "OS do ERP",
    modelos: "Modelos de Checklist",
    grupos: "Grupos de Veículos",
    registrar: "Registrar manutenção",
    custos: "Custos de manutenção",
    nova: "Nova manutenção",
    placa: "Histórico da placa",
  };
  const FILHA: Record<string, string> = {
    ordens: "Ordem de serviço",
    checklists: "Checklist",
    erp: "OS do ERP",
    modelos: "Modelo de checklist",
    grupos: "Grupo de veículos",
    placa: b ? decodeURIComponent(b).toUpperCase() : "Histórico da placa",
  };
  const naBarra = ["preventiva", "ordens", "checklists"].includes(a);
  if (b) return { title: FILHA[a] ?? TOPO[a] ?? "Manutenção", child: true };
  // telas do "Mais" e de fluxo (nova, registrar) voltam para trás; as da barra não
  return { title: TOPO[a] ?? "Manutenção", child: !naBarra };
}
